<template>
  <div class="question-answered-component-container">
    <div
      v-if="!showUserDifficultyLabels"
      :class="{ withStats: showMetadata }"
      class="answer-status"
    >
      <svg
        v-if="correct"
        class="checkmark"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 52 52"
      >
        <circle
          class="checkmark__circle"
          cx="26"
          cy="26"
          r="25"
          fill="none"
        />
        <path
          class="checkmark__check"
          fill="none"
          d="M14.1 27.2l7.1 7.2 16.7-16.8"
        />
      </svg>
      <div
        v-if="!correct"
        :class="{correct: correct}"
        class="icon"
      />
      <tour-tip :tourtip-name="'correctness'">
        {{ correct ? correctText : 'Incorrect' }}
      </tour-tip>
      <flashcard-metadata
        v-if="showMetadata"
        :metadata="metadata"
      />
    </div>
    <div
      v-else
      :class="{ withStats: showMetadata }"
      class="ab-answer-status-wrapper"
    >
      <div class="ab-answer-status">
        <svg
          v-if="correct"
          class="checkmark"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 52 52"
        >
          <circle
            class="checkmark__circle"
            cx="26"
            cy="26"
            r="25"
            fill="none"
          />
          <path
            class="checkmark__check"
            fill="none"
            d="M14.1 27.2l7.1 7.2 16.7-16.8"
          />
        </svg>
        <div
          v-if="!correct"
          :class="{correct: correct}"
          class="icon"
        />
        <tour-tip :tourtip-name="'correctness'">
          {{ correct ? correctText : 'Incorrect' }}
        </tour-tip>
      </div>
      <flashcard-metadata
        v-if="showMetadata"
        :metadata="metadata"
      />
    </div>
    <question-accordion
      v-if="shouldShowQuestionAccordion"
      class="question-accordion"
      :content="flashcardQuestion"
      :show-preview="true"
      title="View Question"
    >
      <template v-slot:before-content>
        <topic
          v-if="embeddedTopic"
          :topic="embeddedTopic"
          :show-title="false"
        />
      </template>
      <template v-slot:after-content>
        <question-accordion
          v-if="casesEnabled && flashcard.case"
          class="question-accordion case"
          :content="flashcard.case.content"
          :images="flashcard.case.caseImages"
          :title="flashcard.case.buttonText"
        />
        <asset-associations-list
          v-if="assetAssociationButtons.length > 0"
          :asset="flashcard"
          context="question"
          @flashcard-aa-clicked="$emit('flashcard-aa-clicked', $event)"
        />
      </template>
    </question-accordion>
  </div>
</template>

<script>
import AssetAssociationsList from '@components/assetAssociations/list';
import FlashcardMetadata from '@components/flashcards/metadata';
import QuestionAccordion from '@components/ui/questionAccordion';
import Topic from '@components/topics/topic';
import TourTip from '@components/singletons/tour-tip';

export default {
  name: 'QuestionAnswered',
  components: { AssetAssociationsList, FlashcardMetadata, QuestionAccordion, Topic, TourTip },
  props: {
    casesEnabled: {
      type: Boolean,
      default: false,
    },
    correct: {
      type: Boolean,
      default: false,
    },
    correctText: {
      type: String,
    },
    flashcard: {
      type: Object,
    },
    metadata: {
      type: Object,
    },
    showMetadata: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showUserDifficultyLabels: false,
    };
  },
  computed: {
    assetAssociationButtons() {
      return (this.flashcard.assetAssociations || []).filter(aa => aa.displayType !== 'embed_before');
    },
    embeddedTopic() {
      if (this.flashcard.assetAssociations) {
        const topicAA = this.flashcard.assetAssociations.find(aa => aa.assetBType === 'Topic' && aa.displayType === 'embed_before');
        if (topicAA) {
          return this.$store.getters['topics/topics'][topicAA.assetBId];
        }
      }
    },
    flashcardQuestion() {
      let question;
      if(this.flashcard.type == 'Cloze / Dropdown') {
        question = '';
      } else {
        question = this.flashcard.question;
      }
      return question;
    },
    shouldShowQuestionAccordion() {
      return this.flashcard.type !== 'Highlight';
    },
  },
  mounted() {
    this.setShowUserDifficultyLabels();
  },
  methods: {
    setShowUserDifficultyLabels() {
      let show = false;
      const user = this.$store.getters['user/get'];
      if (user?.previewer) {
        show = true;
      } else {
        const apptimizeEnabled = this.$apptimize.checkExperiments({
          experimentName: 'Enable Question Difficulty Labels',
          bool: 'enableQuestionDifficultyLabels',
          defaultVal: false,
          matcher: /Enable Question Difficulty Labels/,
        });
        show = apptimizeEnabled;
      }

      this.showUserDifficultyLabels = show;
    },
  },
};
</script>

<style lang="scss" scoped>
.checkmark__circle {
  stroke-dasharray: 166;
  stroke-dashoffset: 166;
  stroke-width: 2;
  stroke-miterlimit: 10;
  stroke: #00b46d;
  fill: none;
  animation: stroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards;
}

.checkmark {
  height: rem-calc(24);
  margin-right: rem-calc(14);
  width: rem-calc(24);
  border-radius: 50%;
  display: block;
  stroke-width: 5;
  stroke: #fff;
  stroke-miterlimit: 10;
  box-shadow: inset 0px 0px 0px #00b46d;
  animation: fill .4s ease-in-out .4s forwards, scale .3s ease-in-out .9s both;
  background-size: contain;
  height: rem-calc(24);
  margin-right: rem-calc(14);
  width: rem-calc(24);
}

.checkmark__check {
  transform-origin: 50% 50%;
  stroke-dasharray: 48;
  stroke-dashoffset: 48;
  animation: stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.8s forwards;
}

@keyframes stroke {
  100% {
    stroke-dashoffset: 0;
  }
}
@keyframes scale {
  0%, 100% {
    transform: none;
  }
  50% {
    transform: scale3d(1.1, 1.1, 1);
  }
}
@keyframes fill {
  100% {
    box-shadow: inset 0px 0px 0px 30px #00b46d;
  }
}
.question-answered-component-container {
  .answer-status {
    align-items: center;
    display: flex;
    font-weight: $bold;
    color: #000000;
    font-size: rem-calc(26);
    margin-bottom: rem-calc(24);

    &.withStats {
      @include breakpoint(small) {
        flex-direction: column;

        .icon {
          margin: 0 0 rem-calc(8) 0;
        }
      }
    }

    .icon {
      @include incorrect-svg(#dc3416);
      background-size: contain;
      height: rem-calc(24);
      margin-right: rem-calc(14);
      width: rem-calc(24);

      &.correct {
        @include correct-svg(#00bc5c);
      }
    }
  }

  .ab-answer-status-wrapper {
    margin-bottom: rem-calc(24);
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: #000000;
    font-size: rem-calc(26);
    font-weight: $bold;

    .ab-answer-status {
      display: flex;
      align-items: center;
    }

    .icon {
      @include incorrect-svg(#dc3416);
      background-size: contain;
      height: rem-calc(24);
      margin-right: rem-calc(14);
      width: rem-calc(24);

      &.correct {
        @include correct-svg(#00bc5c);
      }
    }

    @include breakpoint(small) {
      align-items: flex-start;
      .ab-answer-status {
        margin-bottom: 1rem;
      }
      &.withStats {
        flex-direction: column;
      }
    }
  }

  .question-accordion {
    margin: rem-calc(16) 0 !important;
  }

  /deep/ .topic-component-container {
    .top-content {
      padding: 0;

      .content {
        margin: 0;
      }
    }
  }
}
</style>
